import { Link, useNavigate } from "react-router";
import {
    CATEGORY_LIST,
    CREATE_PRODUCT,
    DIAGNOSTICS_HIDDEN_PLATFORMS,
    DIAGNOSTICS_PERMISSION,
    PRODUCT_CREATE,
    PRODUCT_DELTA_CATEGORY_LIST,
    PRODUCT_DIAGNOSTICS,
    DELETED_FROM_ALL_ENVIRONMENTS,
    DELETED_FROM_CURRENT_ENVIRONMENT,
    FULL_DELETE_PERMISSION,
} from "../ProductConstants";
import Search from "Components/DesignSystem/Search/Search";
import IconButton from "Components/DesignSystem/IconButton/IconButton";
import debounce from "lodash/debounce";

import { MouseEventHandler, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BulkAction } from "./BulkAction";
import ProductFilter from "./ProductFilter";
import ProductSort from "./ProductSort";
import { TAttribute, TProductDeltaHeaderProps } from "types/Pages/ProductDelta";
import { ProductsContext } from "../ProductsContext";
import CreateEditParentDialog from "./CreateEditParentDialog";
import MoveToParentDialog from "./MoveToParentDialog";
import ReactDOM from "react-dom"
import ServiceWrapper from "Services/ServiceWrapper";
import AuthManager from "Common/AuthManager";
import AppSettings from "Settings/AppSettings";
import Breadcrumbs from "Components/DesignSystem/Breadcrumbs/Breadcrumbs";
import Button from "Components/DesignSystem/Button/Button";
import Alert from "Components/DesignSystem/Alert/Alert";
import Skeleton from "Components/DesignSystem/Skeleton/Skeleton";
/* § 116: this file used to straddle both toast systems — the create-permission
   denial on the DS store (§ 64), everything else on the old one — which is why the
   DS import carried an alias to tell them apart. One system now, one name. */
import { toast } from "Components/DesignSystem/Toast/useToast";
import ProductDeleteConfirmationModal from "../components/ProductDeleteConfirmModal";
import CreatePDW from "../PDW/CreatePDW";
import UpdatePDW from "../PDW/UpdatePDW";
import ValidatePDW from "../PDW/ValidatePDW";
import LogsPDW from "../PDW/LogsPDW";
import TemplatePDW from "../PDW/TemplatePDW";
import { AppContext } from "Context/AppContext";
import ProductCategoryDropDown from "./ProductCategoryDropDown";
import isEqual from "lodash/isEqual";
import { createParent } from "api/Parent";
import BulkDeleteXLSModal from "../PDW/BulkDeleteXLS";
import DeleteParentModal from "./DeleteParentModal";
import RemoveChildModal from "./RemoveChildModal";
import { XIcon } from "assests/GmsIcons";
import React from "react";
import RecategorizeModal from "../Recategorization/components/RecategorizeModal";
import CustomAttributeTag from 'Components/Common/CustomAttributeTag';

/** The counts `POST /product/bulk_delete` returns (sync) or its job finishes with (async). */
interface BulkDeleteResult {
    count: number;
    completely_deleted?: number;
    channel_mapping_removed?: number;
    failed: number;
    total_processed: number;
    all_environments?: boolean;
    failed_products?: { product_id?: string; product_name?: string; reason?: string }[];
}

const ProductDeltaHeader = ({
    handleSearchChange,
    categoryid,
    filters,
    setFilters,
    updateFilters,
    totalProducts,
    dataLoading,
    setDataLoading,
    productSelected,
    setProductSelected,
    childSelected,
    setChildSelected,
    moveToParentModal,
    setMoveToParentModal,
    createParentModal,
    setCreateParentModal,
    ...props
}: TProductDeltaHeaderProps) => {
    const navigate = useNavigate();

    /* The toolbar search on DS `Search` (§ 70). The debounce that lived inside
       CustomSearchBar (600ms default) moves here — same net timing — with a
       latest-ref so the memoized debounce never freezes a stale handler (the
       CustomSearchBar bug shape, okf/products § 7.1). */
    const [productQuery, setProductQuery] = useState<string>("");
    /*
      § 288 — the debounce window. `dataLoading` starts with the REQUEST, 600ms after
      the keystroke; nothing else here knows a search is coming. Cleared inside the
      debounced call, so the two hand over with no gap between them.
    */
    const [searchPending, setSearchPending] = useState<boolean>(false);
    const searchHandlerRef = useRef(handleSearchChange);
    searchHandlerRef.current = handleSearchChange;
    const debouncedProductSearch = useMemo(() => debounce((value: string) => {
        setSearchPending(false);
        searchHandlerRef.current(value);
    }, 600), []);
    useEffect(() => () => { debouncedProductSearch.cancel(); }, [debouncedProductSearch]);
    const onProductQueryChange = (value: string) => {
        const term = value.trimStart();
        setProductQuery(term);
        setSearchPending(true);
        debouncedProductSearch(term);
    };
    let appData = useContext(AppContext);
    let industryId = appData?.get_selected_industry()?.id ?? "";
    const domainName: string = appData?.get_selected_industry()?.name ?? "";
    const channelKey = appData?.channelKey;
    const createPermission = appData.pm.checkPermission("gmsproducts_create");
    // Hidden outright (not disabled) on BigCommerce and Shopify environments, for every role
    // (US 15428; Shopify added 2026-10-15).
    const diagnosticsPermission = appData.pm.checkPermission(DIAGNOSTICS_PERMISSION)
        && !DIAGNOSTICS_HIDDEN_PLATFORMS.includes(appData?.envPlatform ?? '');
    const fullDeletePermission = appData.pm.checkPermission(FULL_DELETE_PERMISSION);
    const prodContext = useContext<any>(ProductsContext)
    const selectedCategoryName = prodContext?.selected_Category?.name ?? '';
    const [search, setSearch] = useState<string>("")
    const [parentId, setParentId] = useState<string | null>(null)
    const [formLoading, setFormLoading] = useState<boolean>(false)
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false)
    const [downloadUrl, setDownloadUrl] = useState("");

    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [bulkDeleteXLSModal, setBulkDeleteXLSModal] = useState<boolean>(false);

    const [categoriesList, setCategoriesList] = useState(null);
    const [categoryTypeList, setCategoryTypeList] = useState(null);

    const [showCreatePopup, setShowCreatePopup] = useState(false);
    const modalCreateOpen = () => setShowCreatePopup(true);
    const modalCreateClose = () => setShowCreatePopup(false);

    const [showUpdatePopup, setShowUpdatePopup] = useState(false);
    const modalUpdateOpen = () => setShowUpdatePopup(true);
    const modalUpdateClose = () => setShowUpdatePopup(false);

    const [showValidatePopup, setShowValidatePopup] = useState(false);
    const modalValidateOpen = () => setShowValidatePopup(true);
    const modalValidateClose = () => setShowValidatePopup(false);

    const [showRecategorizePopup, setShowRecategorizePopup] = useState(false);
    const modalRecategorizeOpen = () => setShowRecategorizePopup(true);
    const modalRecategorizeClose = () => {
        setDataLoading(true)
        setShowRecategorizePopup(false)
        setChildSelected([])
        setProductSelected([])
        setParentId(null)
        setTimeout(() => {
            props.fetchData()
            setDataLoading(false)
        }, 5000);
    };

    const [showLogs, setShowLogs] = useState(false);
    const modalLogsOpen = () => setShowLogs(true);
    const modalLogsClose = () => setShowLogs(false);
    const [showTemplate, setShowTemplate] = useState(false);
    const modalTemplateOpen = () => setShowTemplate(true);
    const openBulkDeleteXLSModal = () => setBulkDeleteXLSModal(true);
    const modalTemplateClose = () => setShowTemplate(false);
    const { fetchAttributes, selectedFilterList } = prodContext
    const api_base_url = AppSettings.GMS_BACKEND_API_URL

    const openDialog = () => {
        setCreateParentModal(false)
        setMoveToParentModal(true);
        setDeleteModal(false)
    };

    const openDeleteModal = () => {
        setCreateParentModal(false)
        setMoveToParentModal(false);
        setDeleteModal(true)
    };

    const openParentModal = () => {
        setCreateParentModal(true)
        setMoveToParentModal(false);
        setDeleteModal(false)
    };

    const closeParentModal = () => {
        setProductSelected([]);
        setCreateParentModal(false);
        setSearch("")
    };

    const closeDialog = () => {
        setProductSelected([]);
        setMoveToParentModal(false);
        setSearch("")
    };

    const closeDeleteModal = () => {
        setDeleteModal(false);
    };

    const handleAttributesChange = (attributes: string[]) => {
        if (!isEqual(filters.filters ?? [], attributes)) updateFilters({ filters: attributes, page: 0 })
    }

    useEffect(() => {
        if (industryId) fetchData();
    }, [industryId]);

    const getSelectedProductListForRecategorization = () => {
        return productSelected?.map(x => { return { id: x?.id, name: x?.name } })
    }

    const [selectedProducts, setSelectedProducts] = useState(getSelectedProductListForRecategorization())

    useEffect(() => {
        setSelectedProducts(getSelectedProductListForRecategorization())
    }, [productSelected])

    // if value is given it means its an array
    const handleFilterDelete = (e: React.MouseEvent, key: string, value?: string) => {
        if (!value) setFilters((prev) => {
            const next = { ...prev }
            delete next[key]
            if (key === "limit" && 'page' in next) delete next['page']
            return next
        })
        else setFilters((prev) => {
            const next = { ...prev }
            const index = next[key].indexOf(value)
            if (index > -1) next[key].splice(index, 1)
            return next
        })
    }

    const handleCreateParent = async (search: string, disabledAttributes: string[]) => {
        const res = await createParent(categoryid, search, [...productSelected.map((i) => i.id)], disabledAttributes)
        if (res[0]) {
            setChildSelected([])
            setProductSelected([])
            toast.success(res[1].message)
            closeParentModal()
            props.fetchData()
        }
    }

    const handleMovetoParent: MouseEventHandler<HTMLButtonElement> = async (e) => {
        if (!categoryid) return toast.error("Category not found")
        if (productSelected.length === 0 && childSelected.length === 0) return toast.error("Select at least one product")
        if (!parentId) return toast.error("Parent should be selected")
        setFormLoading(true)

        const res = await ServiceWrapper.doPut(
            api_base_url + "product/change_or_assign_parent",
            {
                new_parent: parentId,
                variants: [...productSelected.map((i) => i.id), ...childSelected.map((i) => i.id)]
            },
            null,
            AuthManager.getGmsToken()
        )

        if (!res.data.done)
            toast.error(res.data.message ?? "Failed to move products to parent")
        else {
            setChildSelected([])
            setProductSelected([])
            setParentId(null)
            toast.success(res.data.message)
            closeDialog()
            props.fetchData()
        }
        setFormLoading(false)
    }

    /* One toast set for both the sync (<=10) response and the async (>10) job result.
       The success line reports what the user ASKED for (the dialog's checkbox) — the
       counts behind it are per-product backend decisions, and "completely deleted"
       also happens on a current-environment delete of a product's last environment.
       A failure shows the backend's own per-product reason: with the box checked a
       product is deleted everywhere, so "missing mapping" is no longer the only cause. */
    const showBulkDeleteToasts = (result: BulkDeleteResult, allEnvironments: boolean) => {
        const { count, failed, total_processed } = result;
        const backendReason = result.failed_products?.find((p) => p.reason)?.reason;

        if (count > 0) {
            toast.success(allEnvironments ? DELETED_FROM_ALL_ENVIRONMENTS : DELETED_FROM_CURRENT_ENVIRONMENT);
        }

        // Warning/Error message for failed products
        if (failed > 0) {
            if (count > 0) {
                // Some succeeded, some failed
                toast.warning('Unable to delete some products.', {
                    description: `Reason: ${failed} product${failed > 1 ? 's' : ''} failed to delete${backendReason ? ` — ${backendReason}` : ' due to missing product to environment mapping'}`,
                });
            } else {
                // All failed
                toast.error('Unable to delete products.', {
                    description: `Reason: ${backendReason ?? 'All products failed to delete due to missing product to environment mapping'}`,
                });
            }
        }

        // Info message if no products were processed
        if (total_processed === 0) {
            toast.error('Unable to delete products.', {
                description: 'Reason: No products were selected for deletion',
            });
        }
    };

    async function checkUploadStatus(jobId: string, allEnvironments: boolean) {
        if (jobId) {
            const ret = await ServiceWrapper.doGet(
                api_base_url + "job/status/" + jobId,
                null,
                null,
                AuthManager.getGmsToken()
            );

            if (!ret.errorfound) {
                if (ret.data.processing) {
                    setDeleteLoading(true)
                    setTimeout(() => {
                        checkUploadStatus(jobId, allEnvironments);
                    }, 2000);
                    return;
                }

                if (ret.data.error) {
                    toast.error(ret.data.status);
                    setDeleteLoading(false)
                    return;
                }
                setDeleteLoading(false)
                closeDeleteModal();
                setProductSelected([])
                setChildSelected([])
                // The finished task carries the same counts as the sync response under `data`.
                if (ret.data.data) {
                    showBulkDeleteToasts(ret.data.data, allEnvironments);
                } else {
                    toast.success(allEnvironments ? DELETED_FROM_ALL_ENVIRONMENTS : DELETED_FROM_CURRENT_ENVIRONMENT);
                }
            }
        } else {
            toast.error("Job Id not found");
            setDeleteLoading(false)
        }
    }

    const handleDelete = async (allEnvironments: boolean) => {
        setDeleteLoading(true);
        const res = await ServiceWrapper.doPost(
            api_base_url + "product/bulk_delete/channel/" + channelKey,
            {
                product_ids: [...productSelected.map((i) => i.id), ...childSelected.map((i) => i.id)],
                all_environments: allEnvironments,
            },
            null,
            AuthManager.getGmsToken()
        );
        setSearch("");
        if (!res.errorfound && res.data?.done === false) {
            // Refused before anything was deleted (e.g. an invalid flag, or the SKU service
            // was unreachable). The body has no counts, so the count-driven toasts below
            // would show nothing at all; surface the reason.
            setDeleteLoading(false);
            toast.error('Unable to delete products.', {
                description: `Reason: ${res.data.message || 'The delete was refused'}`,
            });
            return;
        }
        if (!res.errorfound) {
            if (res.data.task_id) {
                checkUploadStatus(res.data.task_id, allEnvironments);
            } else {
                props.fetchData();
                closeDeleteModal();
                setDeleteLoading(false);
                setProductSelected([]);
                setChildSelected([]);
                showBulkDeleteToasts(res.data, allEnvironments);
            }
        } else {
            setDeleteLoading(false);
            toast.error('Unable to delete products.', {
                description: 'Reason: An error occurred while deleting products',
            });
        }
    };

    async function fetchData() {
        Promise.all([load_categories(), load_category_types()])
            .then((res) => {
            })
            .catch((_err) => {
            });
    }

    async function load_categories(): Promise<any> {
        return ServiceWrapper.doGet(
            `${api_base_url}category/getbyindustryid/${industryId}`,
            null,
            null,
            AuthManager.getGmsToken()
        ).then(res => {
            if (res.errorfound) {
                toast.error("Error while Fetching categories details");
                return null;
            }
            if (!res.data.done) { toast.error(res.data.message); return null; }

            const categories = res.data.data.reduce((acc: any, category: any) => {
                (acc[category.cattype] = acc[category.cattype] || []).push(category);
                return acc;
            }, {});

            setCategoriesList(categories);
            return categories;
        });
    }

    async function load_category_types(): Promise<any> {
        return ServiceWrapper.doGet(
            `${api_base_url}categorytype/getbyindustryid/${industryId}`,
            null,
            null,
            AuthManager.getGmsToken()
        ).then(res => {
            if (res.errorfound) { toast.error("Error while Fetching categories details"); return null; }
            if (!res.data.done) { toast.error(res.data.message); return null; }

            setCategoryTypeList(res.data.data);
            return res.data.data;
        });
    }

    async function generatePDW() {
        setDownloadUrl("");
        var pdwUrl = `${api_base_url}delete_pdw/download/${categoryid}/${channelKey}`;
        let res = await ServiceWrapper.doGet(
            pdwUrl,
            null,
            null,
            AuthManager.getGmsToken()
        );
        if (!res.errorfound) {
            if (res.data.done) {
                setDownloadUrl(res.data.data);
                toast.success("Downloaded successfully");
            }
        } else {
            toast.error("Download failed");
        }
        setTimeout(() => {
            setDownloadUrl("");
        }, 2000);
    }

    const fetchTableDataAndAttributes = () => {
        fetchAttributes()
        props.fetchData()
    }

    const modalRoot = document.querySelector('#modal-root');

    return (
        <>
            {/* DS Breadcrumbs (§ 69) — this component was originally EXTRACTED from this
                screen's hand-roll (§ 52), which had since drifted: the category-type crumb
                was a fake <Link to="#"> under pointer-events-none. It is a non-navigable
                waypoint, so it rides the § 69 span form. Same conditional shape as before:
                the type crumb only exists once resolvable, the current-page crumb shows an
                empty label while loading. */}
            <Breadcrumbs
                id="products-breadcrumb"
                data-testid="products-breadcrumb"
                items={[
                    {
                        label: CATEGORY_LIST,
                        href: PRODUCT_DELTA_CATEGORY_LIST,
                        onClick: (e) => { e.preventDefault(); navigate(PRODUCT_DELTA_CATEGORY_LIST); },
                        'data-testid': 'products-breadcrumb-link-categories',
                    },
                    ...(!dataLoading && prodContext.findSelectedCategoryTypeName(prodContext.categoryData, categoryid)
                        ? [{ label: prodContext.findSelectedCategoryTypeName(prodContext.categoryData, categoryid) }]
                        : []),
                    { label: !dataLoading ? selectedCategoryName : "" },
                ]}
            />
            <div className={`tw-flex tw-justify-between py-3`}>
                {/* items-center: without it the back Link stretches and its 8x14 chevron
                    pins to the TOP of the strip while the name/pill row sits lower. */}
                <div className={`tw-mt-3 tw-flex tw-items-center`}>
                    <Link id="products-btn-back" data-testid="products-btn-back" to={PRODUCT_DELTA_CATEGORY_LIST}
                        aria-label="Back to categories" className={`tw-flex tw-items-center tw-text-ink`}>
                        {/* Inline copy of assests/img/back-icon.svg — an <img> can't take currentColor, so it can't theme */}
                        <svg className={`tw-cursor-pointer`} width="8" height="14" viewBox="0 0 8 14" fill="none" aria-hidden="true">
                            <path d="M7 1L0.999999 7L7 13" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </Link>
                    <div className={`tw-flex tw-flex-row tw-py-0 tw-px-2.5 tw-items-center tw-justify-center tw-gap-[10px] tw-text-5xl`}>
                        <ProductCategoryDropDown fetchData={props.fetchData} setFilters={setFilters} />
                        <div className={`tw-rounded-[44px] tw-bg-surface-sunken tw-min-h-[26px] tw-flex tw-flex-row tw-py-0.5 tw-px-3 tw-box-border tw-items-center tw-justify-center tw-shrink-0`}>
                            {/* § 62 grammar: the DS shimmer, not a pulse — this file is off the
                                grammar guard's legacy allowlist as of § 69. */}
                            {dataLoading
                                ? <Skeleton variant="text" width="72px" data-testid="products-count-loading" />
                                : <div className={`tw-text-sm tw-font-normal tw-text-ink-secondary tw-whitespace-nowrap`}>{totalProducts} Products</div>}
                        </div>
                    </div>
                </div>
                <div className={`tw-ps-3`}>
                    <Button id="products-btn-create" data-testid="products-create-btn-list" onClick={() => { if (createPermission) { navigate(`${PRODUCT_CREATE}?categoryid=${categoryid}`) } else { toast.info("You don't have permission to Create a Product") } }}>{CREATE_PRODUCT}</Button>
                </div>
            </div>
            {domainName && (
                <Alert
                    tone="info"
                    className="tw-mb-3 tw-border tw-border-tone-info-glyph"
                    data-testid="products-domain-context"
                >
                    Products available in this <strong data-testid="products-domain-context-name">{domainName}</strong> domain
                </Alert>
            )}
            {/* `tw-border-edge` (2026-09-09, user screenshot in dark): the rule under the header
                strip carried no colour class, so it painted Tailwind's DEFAULT border colour —
                the light-grey literal #DFE0E6 — a bright line across the dark page. The
                default itself now rides the edge token (DS § 250), and this rule names it
                explicitly. The "Filter" label and the chips were the same class of literal
                (#6B778C, #505F79, #DFE1E6, white) — role tokens now; handlers untouched. */}
            <div id="products-filter-bar" className={`tw-flex tw-py-1.5 ${selectedFilterList.current.length !== 0 ? 'tw-border-y' : 'tw-border-t'} tw-border-edge tw-overflow-x-scroll no-scrollbar`}>
                {selectedFilterList.current.length !== 0 ? <div className={`tw-flex tw-items-center tw-whitespace-nowrap  tw-w-auto`}>
                    <label htmlFor="" className={`tw-font-semibold tw-text-xs tw-me-3 tw-text-ink-tertiary`}>Filter</label>

                    {selectedFilterList.current?.map((attribute: TAttribute, attrIndex: number) => (
                        attribute.values.map((value, valueIndex) => (
                            <div key={value.id} id={`products-filter-chip-${attrIndex}-${valueIndex}`} className={`tw-rounded tw-w-auto tw-bg-surface tw-flex tw-justify-between tw-my-2 tw-py-1 tw-px-2 tw-me-3 tw-items-center tw-gap-[10px] tw-border tw-border-solid tw-border-edge`}>
                                <label className={`tw-text-ink-secondary tw-font-medium tw-text-xs tw-flex tw-items-center tw-gap-1`}>
                                    {/* AB#14859 — same prefix placement as the filter drawer above. */}
                                    {attribute.is_custom === true &&
                                        <CustomAttributeTag data-testid={`filter-chip-custom-tag-${attribute.id}`} size={12} />}
                                    {attribute.name}: {value.value}
                                </label>
                                {/* `icon-follow-ink`: GmsIcons' XIcon bakes stroke="#505F79" — invisible in dark
                                    without the re-ink; the button's ink is what the × now paints in. */}
                                <button aria-label="Close" data-testid={`productlist-productdeltaheader-button-1-${valueIndex}`} id={`products-filter-chip-${attrIndex}-${valueIndex}-btn-remove`} className="icon-follow-ink tw-flex tw-items-center tw-text-ink-secondary hover:tw-text-ink" onClick={(e) => {
                                    e.preventDefault()
                                    prodContext.selectedFilterList.current = prodContext.toggleAttributeValueInPlace(prodContext.selectedFilterList.current, attribute.id, attribute.name, value.id, value.value);
                                    handleFilterDelete(e, "filters", value.id)
                                }}><XIcon /></button>
                            </div>
                        ))
                    ))}

                </div> : null}
            </div>
            <div id="products-toolbar" className={`tw-flex tw-flex-wrap tw-items-center tw-justify-end`}>
                <div className={`tw-w-64`}>
                    <Search
                        value={productQuery}
                        onChange={onProductQueryChange}
                        /* § 288 — the debounce window, then the product fetch.
                           `dataLoading` is the TABLE's flag, so it also covers a sort,
                           a filter or a page change; that reads honestly enough — the
                           list under the field is being fetched either way — and the
                           page owns no finer-grained flag to borrow. */
                        loading={searchPending || dataLoading}
                        loadingLabel="Searching products"
                        placeholder="Search Products"
                        aria-label="Search products"
                        autoComplete="off"
                        data-testid="products-search-input"
                    />
                </div>
                <ProductSort filters={filters} setFilters={setFilters} />
                <ProductFilter
                    handleAttributesChange={handleAttributesChange}
                    filters={filters}
                />
                <BulkAction
                    filters={filters}
                    productsSelected={productSelected}
                    childSelected={childSelected}
                    openDeleteModal={openDeleteModal}
                    onOpenCreateModal={modalCreateOpen}
                    onOpenUpdateModal={modalUpdateOpen}
                    onOpenValidateModal={modalValidateOpen}
                    onOpenRecategorizeModal={modalRecategorizeOpen}
                    onOpenLogsModal={modalLogsOpen}
                    onOpenTemplateModal={modalTemplateOpen}
                    downloadTemplate={generatePDW}
                    openBulkDeleteXLSModal={openBulkDeleteXLSModal}
                />
                {diagnosticsPermission && (
                    <IconButton
                        id="products-btn-diagnostics"
                        data-testid="products-btn-diagnostics"
                        variant="subtle"
                        aria-label="Diagnostics"
                        title="Diagnostics"
                        className="tw-ml-3"
                        onClick={() => navigate(`${PRODUCT_DIAGNOSTICS}/${categoryid}`)}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <circle cx="10.5" cy="10.5" r="8.25" stroke="currentColor" strokeWidth="1.75" />
                            <path d="M16.6 16.6L21 21" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                            <path d="M5.5 10.75h2l1.5-3 2.25 6 1.75-4 1 1h1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </IconButton>
                )}

            </div>
            {
                (moveToParentModal && modalRoot) &&
                ReactDOM.createPortal(<MoveToParentDialog
                    categoryid={categoryid}
                    onClose={closeDialog}
                    search={search}
                    setSearch={setSearch}
                    handleSubmit={handleMovetoParent}
                    loading={formLoading}
                    setParentId={setParentId}
                />,
                    modalRoot)
            }


            <BulkDeleteXLSModal
                isOpen={bulkDeleteXLSModal}
                onClose={() => setBulkDeleteXLSModal(false)}
                fetchData={props.fetchData}
            />

            <CreateEditParentDialog
                heading="Create Parent"
                onClose={closeParentModal}
                handleSubmit={handleCreateParent}
                isOpen={createParentModal}
                buttonLabel="Create"
            />
            {
                (showCreatePopup && modalRoot) &&
                ReactDOM.createPortal(<CreatePDW
                    fetchData={fetchTableDataAndAttributes}
                    showCreatePopup={showCreatePopup}
                    modalCreateClose={modalCreateClose}
                />,
                    modalRoot
                )
            }

            {
                (showUpdatePopup && modalRoot) &&
                ReactDOM.createPortal(<UpdatePDW
                    fetchData={fetchTableDataAndAttributes}
                    showUpdatePopup={showUpdatePopup}
                    modalUpdateClose={modalUpdateClose}
                />,
                    modalRoot
                )
            }

            {
                (showValidatePopup && modalRoot) &&
                ReactDOM.createPortal(<ValidatePDW
                    showValidatePopup={showValidatePopup}
                    modalValidateClose={modalValidateClose}
                />,
                    modalRoot
                )
            }
            {
                (showRecategorizePopup && modalRoot) &&
                ReactDOM.createPortal(<RecategorizeModal
                    showRecategorizePopup={showRecategorizePopup}
                    modalRecategorizeClose={modalRecategorizeClose}
                    selectedProducts={selectedProducts}
                    setSelectedProducts={setSelectedProducts}
                />,
                    modalRoot
                )
            }

            {
                (showLogs && modalRoot) &&
                ReactDOM.createPortal(<LogsPDW
                    showLogs={showLogs} modalLogsClose={modalLogsClose}
                />,
                    modalRoot
                )
            }

            {
                (showTemplate && modalRoot) &&
                ReactDOM.createPortal(<TemplatePDW
                    showPopup={showTemplate}
                    modalClose={modalTemplateClose}
                    categoriesList={categoriesList}
                    categoryTypeList={categoryTypeList}
                />,
                    modalRoot
                )
            }
            <ProductDeleteConfirmationModal
                showModal={Boolean(deleteModal && (childSelected && productSelected) && modalRoot)}
                closeModal={closeDeleteModal}
                loading={deleteLoading}
                totalProducts={childSelected?.length + productSelected?.length}
                onConfirm={handleDelete}
                canDeleteAllEnvironments={fullDeletePermission}
            />

            <RemoveChildModal
                isOpen={prodContext.removeChildModal}
                onClose={() => prodContext.setRemoveChildModal(false)}
                childSelected={childSelected}
                setChildSelected={setChildSelected}
                fetchData={fetchData}
            />

            <DeleteParentModal
                isOpen={!!prodContext.deleteParent}
                onClose={() => prodContext.setDeleteParent(null)}
                fetchData={fetchData}
            />

            <iframe
                style={{ display: "none" }}
                src={downloadUrl}

                id="iframe_pdw_upload"
                title="Delete PDW"
            />

        </>
    );
}

export default ProductDeltaHeader